import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Sparkles, Headphones, MessageCircle, Flame, Check, Send, Volume2, VolumeX, RotateCcw } from 'lucide-react';
import { useClickSound } from '../hooks/useClickSound';
import { TOOL_COSTS } from '../hooks/useEnergyManager';
interface BeliefDetoxProps {
  energyPoints: number;
  onDeductPoints: (amount: number) => void;
  onClose: () => void;
  onComplete: () => void;
}
type Phase = 'tutorial' | 'intention' | 'chat' | 'analyzing' | 'burn' | 'new_code';
interface Message {
  id: string;
  role: 'ai' | 'user';
  content: string;
}
interface BeliefResult {
  belief: string;
  code: string;
}
interface IntentionArea {
  id: string;
  emoji: string;
  label: string;
  desc: string;
  color: string;
}
const INTENTION_AREAS: IntentionArea[] = [{
  id: 'relacje',
  emoji: '❤️',
  label: 'Relacje',
  desc: 'Miłość, bliskość, zaufanie',
  color: 'rose'
}, {
  id: 'obfitosc',
  emoji: '💰',
  label: 'Obfitość / Kariera',
  desc: 'Pieniądze, sukces, cel',
  color: 'amber'
}, {
  id: 'akceptacja',
  emoji: '🧘‍♀️',
  label: 'Akceptacja siebie',
  desc: 'Wartość, pewność, tożsamość',
  color: 'violet'
}, {
  id: 'bezpieczenstwo',
  emoji: '🛡️',
  label: 'Poczucie bezpieczeństwa',
  desc: 'Stabilność, kontrola, lęk',
  color: 'sky'
}];
const FALLBACK_RESULTS: Record<string, BeliefResult> = {
  relacje: {
    belief: 'Boję się, że jeśli pokażę swoją wrażliwość, zostanę zraniona.',
    code: 'Moja wrażliwość jest moją siłą i przyciąga prawdziwą miłość.'
  },
  obfitosc: {
    belief: 'Nie zasługuję na obfitość, bo nie jestem wystarczająco dobra.',
    code: 'Zasługuję na obfitość i z łatwością przyciągam sukces do swojego życia.'
  },
  akceptacja: {
    belief: 'Muszę być idealna, żeby zasłużyć na miłość i akceptację.',
    code: 'Zasługuję na bezwarunkową miłość dokładnie taka, jaka jestem.'
  },
  bezpieczenstwo: {
    belief: 'Jeśli odpuszczę kontrolę, wszystko się zawali.',
    code: 'Ufam procesowi życia. Odpuszczanie przynosi mi spokój i wolność.'
  }
};
const AREA_GREETINGS: Record<string, string> = {
  relacje: 'Witaj. Weź głęboki oddech i poczuj swoje ciało. Dziś pracujemy z obszarem relacji. Powiedz mi — co w Twoich relacjach z ludźmi sprawia Ci teraz największy ból lub niepokój?',
  obfitosc: 'Witaj. Weź głęboki oddech i poczuj swoje ciało. Dziś pracujemy z obszarem obfitości i kariery. Powiedz mi — co w kwestii pieniędzy, pracy lub sukcesu najbardziej Cię teraz blokuje?',
  akceptacja: 'Witaj. Weź głęboki oddech i poczuj swoje ciało. Dziś pracujemy z akceptacją siebie. Powiedz mi — co w sobie najtrudniej Ci zaakceptować?',
  bezpieczenstwo: 'Witaj. Weź głęboki oddech i poczuj swoje ciało. Dziś pracujemy z poczuciem bezpieczeństwa. Powiedz mi — czego najbardziej się teraz obawiasz?'
};
function getChatSystemPrompt(area: string): string {
  const areaLabel = INTENTION_AREAS.find(a => a.id === area)?.label || area;
  return `Jesteś empatycznym terapeutą metody Theta Healing. Twoim celem jest użycie techniki "Kopania" (Digging), aby odkryć rdzenne, ograniczające przekonanie (Core Belief) użytkownika w obszarze: ${areaLabel}.
Zasady:
1. Twoje wiadomości muszą być BARDZO KRÓTKIE (max 1-2 zdania).
2. Nie oceniaj, nie doradzaj i nie pocieszaj. Twoim jedynym zadaniem jest zadawanie pytań drążących, by dotrzeć do lęku na samym dnie.
3. Przykłady pytań drążących: "Gdyby to się wydarzyło, co najgorszego mogłoby cię spotkać?", "Co to mówi o tobie?", "Jaką korzyść ma dla ciebie trzymanie się tego lęku?".
4. ZAWSZE odnoś się bezpośrednio do poprzedniej odpowiedzi użytkownika.
5. ZABEZPIECZENIE: Jeśli użytkownik odpowiada zdawkowo, bez sensu lub ucieka od odpowiedzi (np. "nie wiem", "aaa", "nic"), nie przechodź dalej. Łagodnie naprowadź go z powrotem: "Zauważam opór. Weź głęboki oddech i pozwól, by pierwsza myśl sama przyszła. Czego tak naprawdę się obawiasz?".
6. WARUNEK KOŃCA: Po maksymalnie 3 lub 4 sensownych wymianach zdań, gdy poczujesz, że znasz już sedno problemu, ZAKOŃCZ rozmowę dodając na samym końcu swojej wiadomości dokładny tag: [KONIEC_SESJI].

WAŻNE: Mów po polsku. Bądź delikatna ale odważna w pytaniach. Twój ton to: ciepły, bezpieczny, mądry.`;
}
const SYNTHESIZER_PROMPT = `Jesteś mistrzem Theta Healing. Przeanalizuj dołączoną transkrypcję krótkiej sesji "Digging". Zidentyfikuj główną blokadę użytkownika i stwórz dla niego nowe, uleczające przekonanie.
Zasady zwrotu:
1. Zwróć wynik WYŁĄCZNIE jako obiekt JSON o podanej niżej strukturze.
2. Oba teksty muszą być w 1. osobie liczby pojedynczej ("Ja").
3. Mają być silne, emocjonalne i krótkie (max 1 zdanie).
4. "new_code" musi być dokładnym, pozytywnym odwróceniem (antidotum) dla "core_belief".

Struktura JSON:
{
  "core_belief": "Wpisz tutaj zidentyfikowane rdzenne przekonanie.",
  "new_code": "Wpisz tutaj afirmację Theta."
}`;
async function callOpenAI(messages: {
  role: string;
  content: string;
}[], jsonMode = false): Promise<string> {
  const body: any = {
    model: 'gpt-4-turbo',
    messages,
    temperature: 0.8,
    max_tokens: 400
  };
  if (jsonMode) {
    body.response_format = {
      type: 'json_object'
    };
  }
  const response = await fetch('https://www.requests.magicpatterns.com/openai/proxy', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(body)
  });
  if (!response.ok) {
    throw new Error(`API error: ${response.status}`);
  }
  const data = await response.json();
  return data.choices?.[0]?.message?.content || '';
}
function parseSynthesizerResult(text: string): BeliefResult | null {
  try {
    const parsed = JSON.parse(text);
    if (parsed.core_belief && parsed.new_code) {
      return {
        belief: parsed.core_belief,
        code: parsed.new_code
      };
    }
  } catch (e) {
    // Try to extract JSON from wrapped response
    try {
      const jsonMatch = text.match(/\{[\s\S]*"core_belief"[\s\S]*"new_code"[\s\S]*\}/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]);
        if (parsed.core_belief && parsed.new_code) {
          return {
            belief: parsed.core_belief,
            code: parsed.new_code
          };
        }
      }
    } catch (e2) {}
  }
  return null;
}
function NewCodePhase({
  result,
  onClose,
  onComplete,
  playClick
}: {
  result: BeliefResult;
  onClose: () => void;
  onComplete: () => void;
  playClick: () => void;
}) {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [hasSpoken, setHasSpoken] = useState(false);
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
  const speakAffirmation = useCallback(() => {
    if (!('speechSynthesis' in window)) return;
    // Cancel any ongoing speech
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(result.code);
    utterance.lang = 'pl-PL';
    utterance.rate = 0.85;
    utterance.pitch = 1.0;
    utterance.volume = 1.0;
    // Try to find a Polish voice
    const voices = window.speechSynthesis.getVoices();
    const polishVoice = voices.find(v => v.lang.startsWith('pl'));
    if (polishVoice) utterance.voice = polishVoice;
    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => {
      setIsSpeaking(false);
      setHasSpoken(true);
    };
    utterance.onerror = () => setIsSpeaking(false);
    utteranceRef.current = utterance;
    window.speechSynthesis.speak(utterance);
  }, [result.code]);
  // Auto-speak on mount after a short delay
  useEffect(() => {
    // Ensure voices are loaded
    const trySpeak = () => {
      if (window.speechSynthesis.getVoices().length > 0) {
        setTimeout(speakAffirmation, 800);
      } else {
        window.speechSynthesis.addEventListener('voiceschanged', () => {
          setTimeout(speakAffirmation, 800);
        }, {
          once: true
        });
      }
    };
    if ('speechSynthesis' in window) {
      trySpeak();
    }
    return () => {
      window.speechSynthesis.cancel();
    };
  }, [speakAffirmation]);
  const toggleSpeech = () => {
    playClick();
    if (isSpeaking) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
    } else {
      speakAffirmation();
    }
  };
  return <motion.div key="new_code" className="flex-1 flex flex-col items-center justify-center p-6 relative" initial={{
    opacity: 0,
    scale: 0.8,
    y: 20
  }} animate={{
    opacity: 1,
    scale: 1,
    y: 0
  }} transition={{
    type: 'spring',
    bounce: 0.5
  }} data-id="element-133">
      <div className="text-center mb-8 absolute top-10" data-id="element-134">
        <span className="text-[10px] uppercase tracking-widest text-emerald-400 font-bold bg-emerald-500/10 px-3 py-1 rounded-full border border-emerald-500/20" data-id="element-135">
          Faza 3: Integracja
        </span>
      </div>

      <div className="w-full max-w-sm bg-gradient-to-br from-emerald-600 to-teal-800 rounded-3xl p-8 border border-emerald-400/30 shadow-[0_0_40px_rgba(16,185,129,0.4)] relative overflow-hidden" data-id="element-136">
        <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-white/20 rounded-full blur-2xl" data-id="element-137" />
        <div className="flex justify-center mb-4" data-id="element-138">
          <div className={`w-12 h-12 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm shadow-inner ${isSpeaking ? 'animate-pulse' : ''}`} data-id="element-139">
            <Sparkles className="w-6 h-6 text-white" data-id="element-140" />
          </div>
        </div>
        <p className="text-xs font-bold text-emerald-200 uppercase tracking-widest mb-2 text-center" data-id="element-141">
          Nowy Kod
        </p>
        <h3 className="text-2xl font-medium text-center text-white mb-4 leading-tight drop-shadow-sm" data-id="element-142">
          "{result.code}"
        </h3>

        {'speechSynthesis' in window && <div className="flex justify-center" data-id="element-143">
            <button onClick={toggleSpeech} className="flex items-center space-x-2 bg-white/15 hover:bg-white/25 backdrop-blur-sm px-4 py-2 rounded-full transition-colors border border-white/20" data-id="element-144">
              {isSpeaking ? <VolumeX className="w-4 h-4 text-white" data-id="element-145" /> : <Volume2 className="w-4 h-4 text-white" data-id="element-146" />}
              <span className="text-xs font-medium text-white" data-id="element-147">
                {isSpeaking ? 'Zatrzymaj' : hasSpoken ? 'Odsłuchaj ponownie' : 'Odsłuchaj'}
              </span>
            </button>
          </div>}
      </div>

      <div className="absolute bottom-10 w-full max-w-xs" data-id="element-148">
        <button onClick={onClose} className="w-full bg-white text-emerald-900 rounded-full py-4 text-sm font-bold hover:bg-slate-100 transition-colors flex items-center justify-center space-x-2 shadow-lg" data-id="element-149">
          <Check className="w-5 h-5" data-id="element-150" />
          <span data-id="element-151">Zakończ Rytuał</span>
        </button>
      </div>
    </motion.div>;
}
export function BeliefDetox({
  energyPoints,
  onDeductPoints,
  onClose,
  onComplete
}: BeliefDetoxProps) {
  const [phase, setPhase] = useState<Phase>('tutorial');
  const [selectedArea, setSelectedArea] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [turnCount, setTurnCount] = useState(0);
  // Burn phase state
  const [burnProgress, setBurnProgress] = useState(0);
  const [isHolding, setIsHolding] = useState(false);
  const [burnComplete, setBurnComplete] = useState(false);
  // Result state
  const [result, setResult] = useState<BeliefResult>(FALLBACK_RESULTS['akceptacja']);
  const {
    playClick,
    playBurn,
    playSuccess
  } = useClickSound();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const holdTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Audio state
  const audioCtxRef = useRef<AudioContext | null>(null);
  const oscillatorsRef = useRef<OscillatorNode[]>([]);
  const gainRef = useRef<GainNode | null>(null);
  // Scroll to bottom of chat
  useEffect(() => {
    if (phase === 'chat') {
      messagesEndRef.current?.scrollIntoView({
        behavior: 'smooth'
      });
    }
  }, [messages, isTyping, phase]);
  // Audio Engine for Binaural Beats (432 Hz + 436 Hz)
  const startAudio = () => {
    try {
      const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
      audioCtxRef.current = new AudioContext();
      gainRef.current = audioCtxRef.current.createGain();
      // Low volume for background
      gainRef.current.gain.setValueAtTime(0, audioCtxRef.current.currentTime);
      gainRef.current.gain.linearRampToValueAtTime(0.15, audioCtxRef.current.currentTime + 2);
      gainRef.current.connect(audioCtxRef.current.destination);
      const freqs = [432, 436];
      oscillatorsRef.current = [];
      freqs.forEach((freq, index) => {
        if (!audioCtxRef.current) return;
        const osc = audioCtxRef.current.createOscillator();
        osc.type = 'sine';
        osc.frequency.value = freq;
        const panner = audioCtxRef.current.createStereoPanner();
        panner.pan.value = index === 0 ? -0.8 : 0.8;
        osc.connect(panner);
        panner.connect(gainRef.current!);
        osc.start();
        oscillatorsRef.current.push(osc);
      });
    } catch (e) {
      console.error('Audio API Error', e);
    }
  };
  const stopAudio = () => {
    if (!audioCtxRef.current || !gainRef.current) return;
    try {
      const ctx = audioCtxRef.current;
      gainRef.current.gain.cancelScheduledValues(ctx.currentTime);
      gainRef.current.gain.setValueAtTime(gainRef.current.gain.value, ctx.currentTime);
      gainRef.current.gain.linearRampToValueAtTime(0.001, ctx.currentTime + 1);
      setTimeout(() => {
        oscillatorsRef.current.forEach(osc => {
          try {
            osc.stop();
            osc.disconnect();
          } catch (e) {}
        });
        oscillatorsRef.current = [];
        if (ctx.state !== 'closed') {
          ctx.close().catch(() => {});
        }
      }, 1100);
    } catch (e) {}
  };
  // Cleanup audio on unmount
  useEffect(() => {
    return () => stopAudio();
  }, []);
  // Burn mechanic logic
  useEffect(() => {
    if (phase === 'burn' && !burnComplete) {
      if (isHolding && burnProgress < 100) {
        playBurn();
        if (navigator.vibrate) navigator.vibrate(50);
        holdTimerRef.current = setInterval(() => {
          setBurnProgress(prev => {
            if (prev >= 100) {
              return 100;
            }
            if (navigator.vibrate) navigator.vibrate(50);
            return prev + 2;
          });
        }, 50);
      } else {
        if (holdTimerRef.current) clearInterval(holdTimerRef.current);
        if (burnProgress < 100 && burnProgress > 0) {
          holdTimerRef.current = setInterval(() => {
            setBurnProgress(prev => {
              if (prev <= 0) {
                if (holdTimerRef.current) clearInterval(holdTimerRef.current);
                return 0;
              }
              return prev - 5;
            });
          }, 50);
        }
      }
    }
    return () => {
      if (holdTimerRef.current) clearInterval(holdTimerRef.current);
    };
  }, [isHolding, burnProgress, phase, burnComplete, playBurn, playSuccess]);
  // Detect burn completion and auto-transition
  useEffect(() => {
    if (burnProgress >= 100 && !burnComplete && phase === 'burn') {
      setIsHolding(false);
      setBurnComplete(true);
      playSuccess();
      if (navigator.vibrate) navigator.vibrate([100, 50, 100, 50, 200]);
      const timer = setTimeout(() => {
        setPhase('new_code');
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [burnProgress, burnComplete, phase, playSuccess]);
  const handleStart = () => {
    playClick();
    if (energyPoints >= 30) {
      onDeductPoints(30);
      startAudio();
      setPhase('intention');
    } else {
      alert('Brak wystarczającej liczby Punktów Energii.');
    }
  };
  const handleSelectArea = (areaId: string) => {
    playClick();
    setSelectedArea(areaId);
    const greeting = AREA_GREETINGS[areaId] || AREA_GREETINGS['akceptacja'];
    setMessages([{
      id: '1',
      role: 'ai',
      content: greeting
    }]);
    setTurnCount(0);
    setPhase('chat');
  };
  const runSynthesizer = async (chatMessages: Message[]) => {
    try {
      // Build transcript from chat (exclude system prompt)
      const transcript = chatMessages.map(m => `${m.role === 'ai' ? 'Terapeuta' : 'Użytkownik'}: ${m.content}`).join('\n');
      const synthMessages = [{
        role: 'system',
        content: SYNTHESIZER_PROMPT
      }, {
        role: 'user',
        content: transcript
      }];
      const synthResponse = await callOpenAI(synthMessages, true);
      const synthResult = parseSynthesizerResult(synthResponse);
      if (synthResult) {
        setResult(synthResult);
      } else {
        // Fallback
        setResult(FALLBACK_RESULTS[selectedArea] || FALLBACK_RESULTS['akceptacja']);
      }
    } catch (error) {
      console.error('Synthesizer error:', error);
      setResult(FALLBACK_RESULTS[selectedArea] || FALLBACK_RESULTS['akceptacja']);
    }
    setTimeout(() => setPhase('burn'), 800);
  };
  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim() || isTyping) return;
    const userMsg = inputValue.trim();
    setInputValue('');
    const newUserMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: userMsg
    };
    const updatedMessages = [...messages, newUserMessage];
    setMessages(updatedMessages);
    setIsTyping(true);
    try {
      const apiMessages = [{
        role: 'system',
        content: getChatSystemPrompt(selectedArea)
      }, ...updatedMessages.map(msg => ({
        role: msg.role === 'ai' ? 'assistant' : 'user',
        content: msg.content
      }))];
      const aiText = await callOpenAI(apiMessages);
      const nextTurn = turnCount + 1;
      // Check for [KONIEC_SESJI] tag
      const isSessionEnd = aiText.includes('[KONIEC_SESJI]');
      const cleanText = aiText.replace(/\[KONIEC_SESJI\]/g, '').trim();
      if (isSessionEnd) {
        // Show final AI message
        const finalMessages = [...updatedMessages, {
          id: Date.now().toString(),
          role: 'ai' as const,
          content: cleanText || 'Rozumiem. Dotarłam do sedna.'
        }];
        setMessages(finalMessages);
        setIsTyping(false);
        setTurnCount(nextTurn);
        // Transition to analyzing phase, then call synthesizer
        setTimeout(() => {
          setPhase('analyzing');
          runSynthesizer(finalMessages);
        }, 2000);
      } else {
        setMessages(prev => [...prev, {
          id: Date.now().toString(),
          role: 'ai',
          content: cleanText
        }]);
        setIsTyping(false);
        setTurnCount(nextTurn);
      }
    } catch (error) {
      console.error('OpenAI API error:', error);
      const nextTurn = turnCount + 1;
      let fallbackResponse = '';
      if (nextTurn === 1) {
        fallbackResponse = 'Rozumiem. Gdyby to się spełniło — co najgorszego mogłoby się stać?';
      } else if (nextTurn === 2) {
        fallbackResponse = 'A gdyby to była prawda... co by to o Tobie mówiło? Kim byłabyś w swoich oczach?';
      } else {
        fallbackResponse = 'Dziękuję za szczerość. Widzę już rdzeń tego, co Cię blokuje.';
      }
      const finalMessages = [...messages, {
        id: Date.now().toString(),
        role: 'user' as const,
        content: inputValue || userMsg
      }, {
        id: (Date.now() + 1).toString(),
        role: 'ai' as const,
        content: fallbackResponse
      }];
      setMessages(finalMessages);
      setIsTyping(false);
      setTurnCount(nextTurn);
      if (nextTurn >= 3) {
        setTimeout(() => {
          setPhase('analyzing');
          setResult(FALLBACK_RESULTS[selectedArea] || FALLBACK_RESULTS['akceptacja']);
          setTimeout(() => setPhase('burn'), 2500);
        }, 2000);
      }
    }
  };
  const handleClose = () => {
    playClick();
    stopAudio();
    onClose();
  };
  return <motion.div className="fixed inset-0 z-50 flex flex-col w-full h-full overflow-hidden bg-slate-900 text-white" initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: 20
  }} data-id="element-152">
      {/* Header */}
      <div className="w-full flex items-center justify-between p-4 z-20 bg-slate-900/80 backdrop-blur-md border-b border-white/10 shrink-0" data-id="element-153">
        <button onClick={handleClose} className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors" data-id="element-154">
          <ArrowLeft className="w-5 h-5" data-id="element-155" />
        </button>
        <div className="flex flex-col items-center" data-id="element-156">
          <span className="text-[10px] text-blue-400 font-bold tracking-widest uppercase" data-id="element-157">
            Theta Healing
          </span>
          <span className="text-sm font-medium" data-id="element-158">Detoks Przekonań</span>
        </div>
        <div className="flex items-center justify-center" data-id="element-159">
          {(phase === 'tutorial' || phase === 'intention') && <div className="flex items-center space-x-1 bg-yellow-500/20 px-2 py-1 rounded-full border border-yellow-500/30" data-id="element-160">
              <Sparkles className="w-3 h-3 text-yellow-400" fill="currentColor" data-id="element-161" />
              <span className="text-xs font-bold text-yellow-400" data-id="element-162">
                {energyPoints}
              </span>
            </div>}
          {selectedArea && (phase === 'chat' || phase === 'analyzing') && <span className="text-[10px] font-bold tracking-wider uppercase bg-white/10 px-2.5 py-1 rounded-full border border-white/20 text-white/70" data-id="element-163">
              {INTENTION_AREAS.find(a => a.id === selectedArea)?.emoji}{' '}
              {INTENTION_AREAS.find(a => a.id === selectedArea)?.label}
            </span>}
        </div>
      </div>

      <div className="flex-1 overflow-hidden relative flex flex-col" data-id="element-164">
        <AnimatePresence mode="wait" data-id="element-165">
          {/* PHASE 1: TUTORIAL */}
          {phase === 'tutorial' && <motion.div key="tutorial" className="flex-1 overflow-y-auto p-6 flex flex-col" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} data-id="element-166">
              <div className="max-w-md mx-auto w-full mt-4" data-id="element-167">
                <div className="w-16 h-16 rounded-2xl bg-blue-500/20 flex items-center justify-center mb-6 border border-blue-500/30 shadow-[0_0_30px_rgba(59,130,246,0.2)]" data-id="element-168">
                  <Sparkles className="w-8 h-8 text-blue-400" data-id="element-169" />
                </div>

                <h1 className="text-2xl font-light mb-2" data-id="element-170">
                  Przeprogramowanie Podświadomości
                </h1>
                <p className="text-sm text-slate-400 mb-8 leading-relaxed" data-id="element-171">
                  Zaraz wejdziesz w głęboki stan plastyczności umysłu. Zobacz,
                  jak będzie wyglądał ten proces.
                </p>

                <div className="space-y-6 mb-10" data-id="element-172">
                  <div className="flex items-start space-x-4" data-id="element-173">
                    <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center shrink-0 mt-0.5 text-blue-400" data-id="element-174">
                      <Headphones className="w-4 h-4" data-id="element-175" />
                    </div>
                    <div data-id="element-176">
                      <h3 className="text-sm font-bold mb-1" data-id="element-177">
                        1. Załóż słuchawki
                      </h3>
                      <p className="text-xs text-slate-400 leading-relaxed" data-id="element-178">
                        Dudnienia różnicowe w tle (częstotliwości w prawym i
                        lewym uchu) płynnie wprowadzą Twój mózg w relaksujący
                        stan fal Theta.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4" data-id="element-179">
                    <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center shrink-0 mt-0.5 text-blue-400" data-id="element-180">
                      <MessageCircle className="w-4 h-4" data-id="element-181" />
                    </div>
                    <div data-id="element-182">
                      <h3 className="text-sm font-bold mb-1" data-id="element-183">
                        2. Kopanie w głąb (Digging)
                      </h3>
                      <p className="text-xs text-slate-400 leading-relaxed" data-id="element-184">
                        Odpowiadaj szczerze na pytania AI na czacie. Znajdziemy
                        ukryty lęk, który jest rdzennym przekonaniem blokującym
                        Twoją energię.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4" data-id="element-185">
                    <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center shrink-0 mt-0.5 text-red-400" data-id="element-186">
                      <Flame className="w-4 h-4" data-id="element-187" />
                    </div>
                    <div data-id="element-188">
                      <h3 className="text-sm font-bold mb-1" data-id="element-189">
                        3. Uwolnij schemat
                      </h3>
                      <p className="text-xs text-slate-400 leading-relaxed" data-id="element-190">
                        Użyj fizycznego gestu. Wciśnij i przytrzymaj przycisk
                        SPAL, aby świadomie zniszczyć zidentyfikowaną blokadę.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4" data-id="element-191">
                    <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center shrink-0 mt-0.5 text-emerald-400" data-id="element-192">
                      <Check className="w-4 h-4" data-id="element-193" />
                    </div>
                    <div data-id="element-194">
                      <h3 className="text-sm font-bold mb-1" data-id="element-195">
                        4. Wgraj Nowy Kod
                      </h3>
                      <p className="text-xs text-slate-400 leading-relaxed" data-id="element-196">
                        Na puste miejsce Twoja podświadomość otrzyma nową,
                        budującą afirmację (Download), która odmieni Twoje
                        reakcje.
                      </p>
                    </div>
                  </div>
                </div>

                <button onClick={handleStart} className="w-full bg-blue-600 hover:bg-blue-500 text-white rounded-xl py-4 text-sm font-bold transition-colors shadow-lg shadow-blue-900/50 flex items-center justify-center space-x-2" data-id="element-197">
                  <span data-id="element-198">Rozumiem, zaczynamy</span>
                  <span className="flex items-center bg-black/20 px-2 py-0.5 rounded-full text-xs" data-id="element-199">
                    -30{' '}
                    <Sparkles className="w-3 h-3 ml-1" fill="currentColor" data-id="element-200" />
                  </span>
                </button>
              </div>
            </motion.div>}

          {/* PHASE 1.5: INTENTION SELECTION */}
          {phase === 'intention' && <motion.div key="intention" className="flex-1 overflow-y-auto p-6 flex flex-col" initial={{
          opacity: 0,
          x: 30
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -30
        }} transition={{
          type: 'spring',
          bounce: 0.25
        }} data-id="element-201">
              <div className="max-w-md mx-auto w-full mt-4 flex-1 flex flex-col" data-id="element-202">
                <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.15
            }} data-id="element-203">
                  <span className="text-[10px] uppercase tracking-widest text-blue-400 font-bold bg-blue-500/10 px-3 py-1 rounded-full border border-blue-500/20" data-id="element-204">
                    Wybór Intencji
                  </span>
                  <h1 className="text-2xl font-light mt-4 mb-2" data-id="element-205">
                    Z czym dziś pracujemy?
                  </h1>
                  <p className="text-sm text-slate-400 mb-8 leading-relaxed" data-id="element-206">
                    Wybierz obszar, który chcesz przepracować. To nada kierunek
                    całej sesji.
                  </p>
                </motion.div>

                <div className="grid grid-cols-2 gap-3" data-id="element-207">
                  {INTENTION_AREAS.map((area, index) => <motion.button key={area.id} initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.2 + index * 0.08,
                type: 'spring',
                bounce: 0.3
              }} onClick={() => handleSelectArea(area.id)} className={`relative group flex flex-col items-center text-center p-5 rounded-2xl border transition-all duration-200 ${area.color === 'rose' ? 'bg-rose-500/10 border-rose-500/20 hover:bg-rose-500/20 hover:border-rose-500/40 hover:shadow-[0_0_25px_rgba(244,63,94,0.15)]' : area.color === 'amber' ? 'bg-amber-500/10 border-amber-500/20 hover:bg-amber-500/20 hover:border-amber-500/40 hover:shadow-[0_0_25px_rgba(245,158,11,0.15)]' : area.color === 'violet' ? 'bg-violet-500/10 border-violet-500/20 hover:bg-violet-500/20 hover:border-violet-500/40 hover:shadow-[0_0_25px_rgba(139,92,246,0.15)]' : 'bg-sky-500/10 border-sky-500/20 hover:bg-sky-500/20 hover:border-sky-500/40 hover:shadow-[0_0_25px_rgba(14,165,233,0.15)]'}`} data-id="element-208">
                      <span className="text-3xl mb-3 group-hover:scale-110 transition-transform" data-id="element-209">
                        {area.emoji}
                      </span>
                      <span className="text-sm font-bold text-white mb-1" data-id="element-210">
                        {area.label}
                      </span>
                      <span className="text-[10px] text-slate-400 leading-tight" data-id="element-211">
                        {area.desc}
                      </span>
                    </motion.button>)}
                </div>
              </div>
            </motion.div>}

          {/* PHASE 2: CHAT */}
          {phase === 'chat' && <motion.div key="chat" className="flex-1 flex flex-col w-full max-w-lg mx-auto relative" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-212">
              {/* Subtle background animation for Theta state */}
              <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-20" data-id="element-213">
                <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-blue-500 rounded-full mix-blend-screen filter blur-[100px] animate-pulse-slow" data-id="element-214"></div>
                <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-teal-500 rounded-full mix-blend-screen filter blur-[100px] animate-pulse-slow" style={{
              animationDelay: '2s'
            }} data-id="element-215"></div>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-4 z-10 pb-24" data-id="element-216">
                <div className="text-center mb-8 mt-4" data-id="element-217">
                  <span className="text-[10px] uppercase tracking-widest text-blue-400 font-bold bg-blue-500/10 px-3 py-1 rounded-full border border-blue-500/20" data-id="element-218">
                    Faza 1: Digging
                  </span>
                </div>

                {messages.map(msg => <motion.div key={msg.id} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-219">
                    <div className={`max-w-[85%] rounded-2xl p-4 text-sm leading-relaxed ${msg.role === 'user' ? 'bg-blue-600 text-white rounded-br-sm' : 'bg-slate-800 text-slate-200 rounded-bl-sm border border-slate-700 shadow-lg'}`} data-id="element-220">
                      {msg.content}
                    </div>
                  </motion.div>)}

                {isTyping && <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} className="flex justify-start" data-id="element-221">
                    <div className="bg-slate-800 rounded-2xl rounded-bl-sm p-4 border border-slate-700 flex space-x-1.5" data-id="element-222">
                      <div className="w-2 h-2 bg-slate-500 rounded-full animate-bounce" style={{
                  animationDelay: '0ms'
                }} data-id="element-223"></div>
                      <div className="w-2 h-2 bg-slate-500 rounded-full animate-bounce" style={{
                  animationDelay: '150ms'
                }} data-id="element-224"></div>
                      <div className="w-2 h-2 bg-slate-500 rounded-full animate-bounce" style={{
                  animationDelay: '300ms'
                }} data-id="element-225"></div>
                    </div>
                  </motion.div>}

                {phase === 'chat' && !isTyping && turnCount >= 3 && messages[messages.length - 1]?.role === 'ai' && <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} className="text-center mt-8 text-xs text-slate-400 animate-pulse" data-id="element-226">
                      Analiza podświadomości...
                    </motion.div>}
                <div ref={messagesEndRef} data-id="element-227" />
              </div>

              <div className="absolute bottom-0 left-0 w-full p-4 bg-gradient-to-t from-slate-900 via-slate-900 to-transparent z-20" data-id="element-228">
                <form onSubmit={handleSendMessage} className="relative max-w-lg mx-auto" data-id="element-229">
                  <input type="text" value={inputValue} onChange={e => setInputValue(e.target.value)} placeholder="Napisz szczerze..." disabled={isTyping || phase !== 'chat'} className="w-full bg-slate-800 border border-slate-700 text-white rounded-full pl-5 pr-12 py-3.5 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all disabled:opacity-50" data-id="element-230" />
                  <button type="submit" disabled={!inputValue.trim() || isTyping || phase !== 'chat'} className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 bg-blue-600 rounded-full flex items-center justify-center text-white disabled:opacity-50 hover:bg-blue-500 transition-colors" data-id="element-231">
                    <Send className="w-4 h-4 ml-0.5" data-id="element-232" />
                  </button>
                </form>
              </div>
            </motion.div>}

          {/* PHASE 2.5: ANALYZING */}
          {phase === 'analyzing' && <motion.div key="analyzing" className="flex-1 flex flex-col items-center justify-center p-6" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} data-id="element-233">
              <div className="flex flex-col items-center text-center" data-id="element-234">
                <motion.div className="w-20 h-20 rounded-full bg-blue-500/20 flex items-center justify-center mb-6 border border-blue-500/30 relative" animate={{
              boxShadow: ['0 0 20px rgba(59,130,246,0.2)', '0 0 40px rgba(59,130,246,0.4)', '0 0 20px rgba(59,130,246,0.2)']
            }} transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-235">
                  <motion.div animate={{
                rotate: 360
              }} transition={{
                duration: 3,
                repeat: Infinity,
                ease: 'linear'
              }} data-id="element-236">
                    <Sparkles className="w-8 h-8 text-blue-400" data-id="element-237" />
                  </motion.div>
                </motion.div>
                <h2 className="text-xl font-light text-white mb-2" data-id="element-238">
                  Analiza podświadomości...
                </h2>
                <p className="text-sm text-slate-400 max-w-xs leading-relaxed" data-id="element-239">
                  Identyfikuję rdzenne przekonanie i tworzę nowy, uleczający
                  kod.
                </p>
                <div className="flex space-x-1.5 mt-6" data-id="element-240">
                  <motion.div className="w-2 h-2 bg-blue-400 rounded-full" animate={{
                opacity: [0.3, 1, 0.3]
              }} transition={{
                duration: 1.2,
                repeat: Infinity,
                delay: 0
              }} data-id="element-241" />
                  <motion.div className="w-2 h-2 bg-blue-400 rounded-full" animate={{
                opacity: [0.3, 1, 0.3]
              }} transition={{
                duration: 1.2,
                repeat: Infinity,
                delay: 0.2
              }} data-id="element-242" />
                  <motion.div className="w-2 h-2 bg-blue-400 rounded-full" animate={{
                opacity: [0.3, 1, 0.3]
              }} transition={{
                duration: 1.2,
                repeat: Infinity,
                delay: 0.4
              }} data-id="element-243" />
                </div>
              </div>
            </motion.div>}

          {/* PHASE 3: BURN */}
          {phase === 'burn' && <motion.div key="burn" className="flex-1 flex flex-col items-center justify-center p-6 relative" initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          filter: 'blur(10px)'
        }} data-id="element-244">
              <div className="text-center mb-8 absolute top-10" data-id="element-245">
                <span className="text-[10px] uppercase tracking-widest text-red-400 font-bold bg-red-500/10 px-3 py-1 rounded-full border border-red-500/20" data-id="element-246">
                  Faza 2: Uwalnianie
                </span>
              </div>

              <AnimatePresence mode="wait" data-id="element-247">
                {!burnComplete ? <motion.div key="burning" className="w-full flex flex-col items-center" exit={{
              opacity: 0,
              scale: 0.8,
              filter: 'blur(10px)'
            }} transition={{
              duration: 0.5
            }} data-id="element-248">
                    <motion.div className="w-full max-w-sm bg-slate-800 rounded-3xl p-8 border border-slate-700 shadow-2xl relative overflow-hidden" animate={{
                scale: isHolding ? 0.95 : 1,
                filter: `sepia(${burnProgress}%) blur(${burnProgress / 20}px) contrast(${1 + burnProgress / 100})`
              }} data-id="element-249">
                      <div className="absolute inset-0 bg-red-500/20 mix-blend-overlay" style={{
                  opacity: burnProgress / 100
                }} data-id="element-250" />
                      <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4 text-center" data-id="element-251">
                        Rozpoznane Przekonanie
                      </p>
                      <h3 className="text-2xl font-medium text-center text-slate-200 mb-6 leading-tight" data-id="element-252">
                        "{result.belief}"
                      </h3>
                      <div className="w-full h-1 bg-slate-700 rounded-full overflow-hidden" data-id="element-253">
                        <div className="h-full bg-red-500 transition-all duration-75" style={{
                    width: `${burnProgress}%`
                  }} data-id="element-254" />
                      </div>
                    </motion.div>
                  </motion.div> : <motion.div key="burned" className="w-full flex flex-col items-center" initial={{
              opacity: 0,
              scale: 0.9,
              y: 20
            }} animate={{
              opacity: 1,
              scale: 1,
              y: 0
            }} transition={{
              type: 'spring',
              bounce: 0.4,
              delay: 0.3
            }} data-id="element-255">
                    <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mb-6 border border-red-500/30 shadow-[0_0_40px_rgba(220,38,38,0.3)]" data-id="element-256">
                      <Flame className="w-10 h-10 text-red-400" data-id="element-257" />
                    </div>
                    <h3 className="text-xl font-light text-white mb-2" data-id="element-258">
                      Przekonanie spalone
                    </h3>
                    <p className="text-sm text-slate-400 text-center max-w-xs animate-pulse" data-id="element-259">
                      Wgrywanie nowego kodu...
                    </p>
                  </motion.div>}
              </AnimatePresence>

              {!burnComplete && <div className="absolute bottom-10 w-full max-w-xs" data-id="element-260">
                  <button onPointerDown={() => {
              playClick();
              setIsHolding(true);
            }} onPointerUp={() => setIsHolding(false)} onPointerLeave={() => setIsHolding(false)} onContextMenu={e => e.preventDefault()} className={`w-full rounded-full py-4 text-sm font-bold transition-all duration-200 flex items-center justify-center space-x-2 select-none touch-none ${isHolding ? 'bg-red-600 text-white scale-95 shadow-[0_0_30px_rgba(220,38,38,0.6)]' : 'bg-slate-800 text-slate-300 border border-slate-700 hover:bg-slate-700'}`} data-id="element-261">
                    <Flame className={`w-5 h-5 ${isHolding ? 'animate-pulse' : ''}`} data-id="element-262" />
                    <span data-id="element-263">
                      {isHolding ? 'Spalanie...' : 'Przytrzymaj by SPALIĆ'}
                    </span>
                  </button>
                </div>}
            </motion.div>}

          {/* PHASE 4: NEW CODE */}
          {phase === 'new_code' && <NewCodePhase result={result} onClose={handleClose} onComplete={onComplete} playClick={playClick} data-id="element-264" />}
        </AnimatePresence>
      </div>
    </motion.div>;
}